import { Section } from "@/layouts/Section";

const H = ({ children }) => (
  <h2 className="text-lg font-semibold text-foreground mb-3">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-base leading-[1.75] text-muted-foreground m-0">{children}</p>
);
const UL = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1 text-base leading-[1.7] text-muted-foreground">
    {items.map((t) => <li key={t}>{t}</li>)}
  </ul>
);

export default function SchoolDataAgreement() {
  return (
    <div>
      <Section variant="light">
        <div className="max-w-[760px] mx-auto">
          <div className="section-label">Legal</div>
          <h1 className="section-heading mb-3">School Data Agreement</h1>
          <p className="text-sm text-muted-foreground m-0">Last updated: April 2026 &mdash; Version 2026-04</p>
        </div>
      </Section>

      <Section variant="muted">
        <div className="max-w-[760px] mx-auto flex flex-col gap-10">
          <div className="space-y-3">
            <P>
              This School Data Agreement (&ldquo;Agreement&rdquo;) is entered into between <strong>EasyReports</strong>,
              operated by Universal Tech Solutions (Business Number: 788359222TZ0002, &ldquo;EasyReports&rdquo;), and the
              school named in the associated order or sign-up (&ldquo;School&rdquo;). Together, EasyReports and the School
              are referred to as the &ldquo;Parties.&rdquo;
            </P>
          </div>

          <div className="space-y-3">
            <H>1. Purpose</H>
            <P>
              This Agreement governs how EasyReports handles student and staff personal information on behalf of the
              School in connection with the EasyReports report card drafting service (&ldquo;the Service&rdquo;).
            </P>
            <P>
              EasyReports is a drafting tool. It is not an official system of record. Reports created in EasyReports
              are working drafts until transferred to the School&rsquo;s official report card system.
            </P>
          </div>

          <div className="space-y-3">
            <H>2. Data Processed</H>
            <P>In connection with the Service, teachers at the School may enter the following categories of data:</P>
            <UL items={[
              "Student first names",
              "Grades, attendance records (absent/late counts), and behavioural indicators",
              "Ontario Education Numbers (OENs), if entered by the teacher",
              "Teacher-authored report card comments",
              "Contextual student information entered into AI generation prompts (see Section 6)",
            ]} />
            <P>EasyReports does not require OEN entry. The decision to enter OENs is made by the individual teacher.</P>
          </div>

          <div className="space-y-3">
            <H>3. Purpose Limitation</H>
            <P>EasyReports will process School data solely for the purpose of providing the Service to the School&rsquo;s teachers. School data will not be used for:</P>
            <UL items={[
              "Analytics beyond what is necessary to operate and maintain the Service",
              "Training or improving AI models",
              "Advertising or marketing purposes",
              "Any commercial purpose beyond providing the Service",
              "Disclosure to any third party except as listed in Section 4",
            ]} />
          </div>

          <div className="space-y-3">
            <H>4. Subprocessors</H>
            <P>EasyReports uses the following subprocessors. Each receives only the data necessary for their specific function.</P>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left text-foreground border-b border-border">
                    <th className="py-2 pr-3 font-semibold">Subprocessor</th>
                    <th className="py-2 pr-3 font-semibold">Location</th>
                    <th className="py-2 pr-3 font-semibold">Function</th>
                    <th className="py-2 font-semibold">Student Data?</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  {[
                    ["MongoDB Atlas", "Toronto, Canada", "Database hosting", "Yes - all core data stored here"],
                    ["DigitalOcean", "Toronto, Canada", "App server hosting", "No - stored in MongoDB Atlas"],
                    ["Google Cloud (Vertex AI)", "Montréal, Canada", "AI comment generation (Gemini)", "Yes - prompts may include student info"],
                    ["Clerk", "United States", "User authentication", "Teacher name and email only"],
                    ["Stripe", "United States", "Payment processing", "No - teacher billing only"],
                    ["Resend", "United States", "Transactional email", "Teacher name and email only"],
                  ].map((row) => (
                    <tr key={row[0]} className="border-b border-border/60 align-top">
                      {row.map((cell, i) => <td key={i} className="py-2 pr-3">{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <P>
              EasyReports will notify the School of any material changes to this subprocessor list prior to those
              changes taking effect. All subprocessors are contractually required to protect personal information to a
              standard consistent with applicable Canadian privacy law.
            </P>
          </div>

          <div className="space-y-3">
            <H>5. Data Residency</H>
            <P>All student data processed in connection with the Service remains in Canada:</P>
            <UL items={[
              "Core report card data (student names, grades, OENs, comments) is stored exclusively on MongoDB Atlas in the Toronto region",
              "AI comment generation is processed by Google Vertex AI in Google Cloud's Montréal, Canada region (northamerica-northeast1)",
              "No student data is transferred outside of Canada at any stage of processing",
            ]} />
            <P>
              No student data is transmitted to subprocessors based in the United States. Clerk and Resend receive
              teacher name and email only, for authentication and email delivery.
            </P>
          </div>

          <div className="space-y-3">
            <H>6. AI Transparency</H>
            <P>
              When teachers use the AI comment generation feature, EasyReports transmits the teacher&rsquo;s prompt to
              Google Vertex AI (Gemini). The prompt is composed by the teacher and may include contextual student
              information such as student names and descriptive notes.
            </P>
            <P><strong>Google Vertex AI&rsquo;s handling of this data:</strong></P>
            <UL items={[
              "Vertex AI processes prompts in Google Cloud's Montréal, Canada region (northamerica-northeast1)",
              "Google does not use Vertex AI prompt or response data to train or improve its foundation models",
              "Prompts are processed only to generate the requested comment and are not used by Google for any other purpose",
            ]} />
            <P><strong>EasyReports&rsquo; handling of this data:</strong></P>
            <UL items={[
              "EasyReports logs AI generation requests (prompt and generated comment) for quota enforcement",
              "These logs are stored in our database (MongoDB Atlas, Toronto) and automatically deleted after 3 years",
              "AI generation logs are not used for any purpose other than quota enforcement",
            ]} />
            <P>
              The School&rsquo;s designated admin is responsible for ensuring that teachers understand what information
              may appropriately be included in AI prompts in accordance with the School&rsquo;s own data policies.
            </P>
          </div>

          <div className="space-y-3">
            <H>7. Security</H>
            <P>EasyReports implements the following technical and organizational safeguards:</P>
            <UL items={[
              "Encryption at rest (MongoDB Atlas managed database, Toronto)",
              "Encryption in transit (HTTPS/TLS for all connections)",
              "Role-based access controls (teachers access only their own reports or reports explicitly shared with them)",
              "Authentication managed by Clerk",
            ]} />
          </div>

          <div className="space-y-3">
            <H>8. Breach Notification</H>
            <P>
              In the event EasyReports discovers a breach of security that affects, or is reasonably likely to affect,
              School data, EasyReports will notify the School&rsquo;s designated contact as soon as feasible; provide a
              description of the nature of the breach, the data affected, and the steps being taken; cooperate with the
              School in any required notifications to affected individuals or regulatory bodies; and maintain records
              of the breach for a minimum of 24 months.
            </P>
          </div>

          <div className="space-y-3">
            <H>9. Teacher and Admin Access Controls</H>
            <P>The School&rsquo;s designated admin is responsible for:</P>
            <UL items={[
              "Managing which teachers are assigned to school seats",
              "Ensuring that all teachers assigned to the school account are authorized to process student data",
              "Removing seat access promptly when a teacher leaves the School",
              "Ensuring teachers understand the School's policies on what student information may be included in AI generation prompts",
            ]} />
            <P>
              EasyReports provides tools to the School admin to assign, unassign, and transfer teacher seats. Teachers
              removed from the school account lose school plan access immediately and retain only free-tier access.
            </P>
          </div>

          <div className="space-y-3">
            <H>10. Data Export and Return</H>
            <P>
              While the School&rsquo;s subscription is active, individual teachers may export their own class data at any
              time through the platform in CSV format. Upon written request from the School, or upon termination of the
              School&rsquo;s subscription, EasyReports will provide instructions and access for a complete export in CSV
              format; deliver a complete export within 14 business days if teacher accounts are inaccessible; confirm in
              writing once delivered; and purge School data within 30 days of export confirmation, except where
              retention is required by applicable law.
            </P>
          </div>

          <div className="space-y-3">
            <H>11. No Sale of Data</H>
            <P>EasyReports will not sell, rent, license, or otherwise monetize School data in any form.</P>
          </div>

          <div className="space-y-3">
            <H>12. Term and Renewal</H>
            <P>
              This Agreement runs concurrently with the School&rsquo;s active EasyReports subscription and renews
              automatically on the same schedule as the subscription. Either Party may terminate this Agreement with 30
              days written notice, subject to the data export and deletion obligations in Section 10.
            </P>
          </div>

          <div className="space-y-3">
            <H>13. Assignment</H>
            <P>
              EasyReports may not assign this Agreement without providing 30 days&rsquo; written notice to the School.
              EasyReports may assign this Agreement to a successor entity in connection with a merger, acquisition, or
              sale of all or substantially all of its assets, provided that the School is notified within 30 days and the
              successor entity agrees to be bound by the terms of this Agreement.
            </P>
          </div>

          <div className="space-y-3">
            <H>14. Severability</H>
            <P>
              If any provision of this Agreement is found to be unenforceable or invalid, that provision will be limited
              or eliminated to the minimum extent necessary so that the remainder of this Agreement continues in full
              force and effect.
            </P>
          </div>

          <div className="space-y-3">
            <H>15. Governing Law</H>
            <P>
              This Agreement is governed by the laws of the Province of Ontario and the federal laws of Canada applicable
              therein.
            </P>
          </div>

          <div className="space-y-3">
            <H>16. Contact</H>
            <P>
              Privacy and data inquiries: privacy@easyreports.ca &nbsp;&middot;&nbsp; General: contact@easyreports.ca
            </P>
          </div>

          <div className="rounded-xl border border-border bg-card px-5 py-4">
            <P>
              <em>
                This Agreement is presented to the School admin at the time of school account creation. By completing
                school account setup, the School admin confirms they have the authority to enter into this Agreement on
                behalf of the School and that the School agrees to its terms. The version accepted, the date of
                acceptance, and the identity of the accepting admin are recorded by EasyReports.
              </em>
            </P>
          </div>
        </div>
      </Section>
    </div>
  );
}
